import React, { useRef, useEffect, useState, useCallback } from 'react';
import '@tensorflow/tfjs-backend-webgl';
import * as poseDetection from '@tensorflow-models/pose-detection';
// TensorFlow.js imports will be done dynamically
import axios from 'axios';
import { speak, initSpeechSynthesis, cancelSpeech } from '../utils/speech';
import SequenceTransition from './SequenceTransition';
import CourseComplete from './CourseComplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POST_SPEECH_ANALYSIS_DELAY = 2000; // Wait 2 seconds after speech before next analysis

interface PoseInfo {
    pose: string;
    score: number;
    feedback: string;
}

interface Props {
  apiEndpoint: string;
    onDetectionChange?: (info: PoseInfo) => void;
    initialCourseData?: any; // Add the new prop for course data
}

// Define WebSocket message types
interface WSMessage {
  type: string;
  data: any;
}

// Define the connections between keypoints for skeleton drawing
const POSE_CONNECTIONS = [
  ['left_ear', 'left_eye'], ['right_ear', 'right_eye'],
  ['left_eye', 'nose'], ['right_eye', 'nose'],
  ['left_shoulder', 'right_shoulder'], ['left_shoulder', 'left_elbow'],
  ['right_shoulder', 'right_elbow'], ['left_elbow', 'left_wrist'],
  ['right_elbow', 'right_wrist'], ['left_shoulder', 'left_hip'],
  ['right_shoulder', 'right_hip'], ['left_hip', 'right_hip'],
  ['left_hip', 'left_knee'], ['right_hip', 'right_knee'],
  ['left_knee', 'left_ankle'], ['right_knee', 'right_ankle']
];

// Add colors for artistic rendering
const GRADIENT_COLORS = [
  '#8a2be2', // Blue Violet
  '#ff0080', // Deep Pink
  '#00bfff', // Deep Sky Blue
  '#32cd32', // Lime Green
  '#ffd700'  // Gold
];

// Add a helper function to get the full URL
const getFullUrl = (path: string, baseUrl: string) => {
  // Check if baseUrl is provided and not empty
  if (baseUrl && !baseUrl.endsWith('/')) {
    baseUrl = baseUrl + '/';
  } else if (!baseUrl) {
    // In production on Vercel, use relative URLs
    if (process.env.NODE_ENV === 'production') {
      baseUrl = '/';
    } else {
      // Default to localhost in development
      baseUrl = 'http://localhost:8000/';
    }
  }
  
  // Remove leading slash from path if baseUrl has trailing slash
  if (path.startsWith('/') && baseUrl.endsWith('/')) {
    path = path.substring(1);
  }
  
  return baseUrl + path;
};

const PoseDetector = ({ apiEndpoint, onDetectionChange, initialCourseData }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [detector, setDetector] = useState<poseDetection.PoseDetector | null>(null);
  const detectorRef = useRef<poseDetection.PoseDetector | null>(null);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  const isDetectingRef = useRef<boolean>(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('Initializing...');
  const [isInitializing, setIsInitializing] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [sequenceCompleteCheckInterval, setSequenceCompleteCheckInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Add WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  
  // Add a correction interval ref
  const correctionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastCorrectionTimeRef = useRef<number>(0);
  
  const animationRef = useRef<number | null>(null);
  const canAnalyze = useRef<boolean>(true);
  const isSpeakingRef = useRef<boolean>(false);
  const speechQueue = useRef<string[]>([]);

  // Track which pose names have already had instructions played (using names instead of IDs to avoid duplicates)
  const playedInstructionsForPoseNames = useRef<Set<string>>(new Set());
  
  // Add refs to track introduction speech
  const introSpeechCompletedRef = useRef<boolean>(false);
  const initialCorrectionSentRef = useRef<boolean>(false);
  
  // Keep track of transition state
  const transitionInProgressRef = useRef<boolean>(false);
  const correctionInProgressRef = useRef<boolean>(false);
  const correctionLockRef = useRef<boolean>(false);
  const MAX_CORRECTION_FAILURES = useRef<number>(0);

  // For UI indication of when the last correction was sent
  const [lastCorrectionTime, setLastCorrectionTime] = useState<number>(0);
  const [detectedPoseName, setDetectedPoseName] = useState<string>('');

  // Make sure these refs are defined in the component:
  const videoRefCanvas = useRef<HTMLCanvasElement>(null);
  
  // Make sure these states are defined within the component:
  const [pose, setPose] = useState<poseDetection.Pose | null>(null);
  const [poseKeypoints, setPoseKeypoints] = useState<any>(null);

  // Add sequence pose state information
  const [currentSequencePose, setCurrentSequencePose] = useState<string | null>(null);
  // Add a ref to persistently track the current pose name
  const currentSequencePoseRef = useRef<string | null>(null);
  // Use the currentPoseIdRef for tracking the active pose ID to prevent stale corrections
  
  // Store current pose data for display
  const [currentPoseData, setCurrentPoseData] = useState<any>(null);
  
  // Add a timer to update the UI countdown
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [remainingTime, setRemainingTime] = useState<number | null>(null);

  // Add transitioning state to show UI feedback
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  // Add sequence complete state
  const [isSequenceComplete, setIsSequenceComplete] = useState<boolean>(false);
  
  // Add feedback state for pose corrections
  const [poseFeedback, setPoseFeedback] = useState<any>(null);

  // Add speech synthesis control state
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(true);

  // Add new tracking ref for poses that have had corrections sent
  // Track which poses we've already sent for correction
  const posesCorrectionRequestedNames = useRef<Set<string>>(new Set());

  // Track if we're waiting for correction response
  const [waitingForCorrection, setWaitingForCorrection] = useState(false);
  const correctionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Add state for sequence transition and course completion
  const [isSequenceTransitioning, setIsSequenceTransitioning] = useState(false);
  const [isCourseComplete, setIsCourseComplete] = useState<boolean>(false);
  const [transitionData, setTransitionData] = useState<any>(null);
  const [courseData, setCourseData] = useState<any>(null);

  // Add state for course importer
  // const [showCourseImporter, setShowCourseImporter] = useState(false);

  // Add new state to track all course data locally
  const [allCourseData, setAllCourseData] = useState<any>(null);
  const [currentSequenceIndex, setCurrentSequenceIndex] = useState<number>(0);
  const [currentPoseIndex, setCurrentPoseIndex] = useState<number>(0);

  // Add another state to track if a course is loaded
  const [isCourseLoaded, setIsCourseLoaded] = useState<boolean>(false);

  // Add a reference to track the currently active pose ID to prevent stale corrections
  const currentPoseIdRef = useRef<string | null>(null); 

  // Refs for tracking state
  const hasGeneratedCorrectionRef = useRef<boolean>(false);
  
  // Add this near the other ref definitions at the top of the component
  const currentPoseRef = useRef<any>(null);
  
  // Function to generate pose correction - moved before its usage
  const generatePoseCorrection = useCallback(async (poseData: any, poseId: string | null) => {
    if (!poseData || !poseId || currentPoseIdRef.current !== poseId) {
      console.log('Skipping correction - invalid state or pose changed');
      return;
    }

    // CRITICAL FIX: Use the ref which has the most up-to-date pose data
    // This gets the ACTUAL current pose at the moment the correction is generated
    const currentPose = currentPoseRef.current;
    const currentKeypoints = currentPose?.keypoints || [];
    
    // Log the current detected keypoints with lower threshold (0.3 instead of 0.5)
    console.log(`Generating correction for ${poseData.name} with ${currentKeypoints.length} keypoints`);
    console.log('Key body parts detected:', currentKeypoints
      .filter((kp: any) => kp.score && kp.score > 0.3) // Lower threshold to 0.3
      .map((kp: any) => kp.name)
      .join(', '));
    
    // Log the full structure of the first few keypoints to debug format
    console.log('Keypoint structure example:', 
      currentKeypoints.slice(0, 3).map((kp: any) => ({
        name: kp.name,
        score: kp.score,
        x: kp.x,
        y: kp.y
      }))
    );
    
    // Create request body with CURRENT pose keypoints and key angles
    const requestBody = {
      pose_name: poseData.name,
      key_angles: poseData.key_angles,
      pose_keypoints: currentKeypoints, // Use current keypoints from ref, not stale state
      needs_correction: poseData.needsCorrection !== false // Default to true if not specified
    };
    
    try {
      const response = await fetch(getFullUrl(`generate_correction`, BACKEND_URL), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.correction && currentPoseIdRef.current === poseId) {
        speak(data.correction);
        
        // Log returned angles and alignment issues for debugging
        if (data.detected_angles) {
          console.log('Detected angles:', data.detected_angles);
        }
        if (data.alignment_issues) {
          console.log('Alignment issues:', data.alignment_issues);
        }
      }
    } catch (error) {
      console.error('Error generating correction:', error);
    }
  }, [BACKEND_URL, speak]); // Remove pose from dependencies, using ref instead

  // Function to schedule a pose correction
  const scheduleCorrection = useCallback((poseData: any) => {
    if (correctionTimeoutRef.current) {
      clearTimeout(correctionTimeoutRef.current);
    }
    
    console.log('Scheduling pose correction for', poseData.name, 'to be generated in 1 second');
    
    correctionTimeoutRef.current = setTimeout(() => {
      correctionTimeoutRef.current = null;
      
      // Double-check that we're still on the same pose before generating correction
      if (isDetectingRef.current && currentPoseData && currentPoseData.name === poseData.name) {
        console.log('Triggering correction generation now - using current pose data');
        // Important: we're passing currentPoseData here, not the potentially stale poseData from the closure
        generatePoseCorrection(currentPoseData, currentPoseIdRef.current);
        hasGeneratedCorrectionRef.current = true;
      } else {
        console.log('Skipping correction - pose changed since scheduling');
      }
    }, 1000);
  }, [currentPoseData, generatePoseCorrection]);

  // WebSocket helper function to safely send messages
  const sendWebSocketMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
      }
    }
  }, []);

  // Function to speak pose instructions
  const speakPoseInstructions = useCallback((poseData: any) => {
    if (!poseData || !isSpeechEnabled) return;
    
    // Generate a unique ID for this pose instance
    const poseInstanceId = `${poseData.name}-${Date.now()}`;
    // Store this as the current valid pose ID
    currentPoseIdRef.current = poseInstanceId;
    
    console.log(`Starting instruction flow for ${poseData.name} with ID: ${poseInstanceId}`);
    console.log('Pose data instructions:', poseData.instructions);
    
    // Cancel any previous speech first
    cancelSpeech();
    
    // Mark as speaking
    setIsSpeaking(true);
    isSpeakingRef.current = true;
    
    // Extract instructions based on different possible formats in the JSON
    let instructionText = `${poseData.name}. `;
    
    if (Array.isArray(poseData.instructions)) {
      // If instructions is an array, join them
      instructionText += poseData.instructions.join('. ');
    } else if (typeof poseData.instructions === 'string' && poseData.instructions.trim()) {
      // If instructions is a non-empty string, use it directly
      instructionText += poseData.instructions;
    } else if (poseData.instruction && typeof poseData.instruction === 'string') {
      // Some JSON might use 'instruction' singular instead of 'instructions'
      instructionText += poseData.instruction;
    } else {
      // Default fallback
      instructionText += `Hold for ${poseData.duration || 30} seconds.`;
    }
    
    console.log(`Speaking instruction text: "${instructionText}"`);
    
    // Speak the instructions
    speak(instructionText, () => {
      // Called when speech completes
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      
      // Verify this is still the active pose by checking the pose ID
      if (currentPoseIdRef.current !== poseInstanceId) {
        console.log(`Aborting correction scheduling for ${poseData.name} - pose has changed (ID mismatch)`);
        return;
      }
      
      // Only generate correction if we haven't already done one for this pose
      // and if the pose needs correction
      if (!hasGeneratedCorrectionRef.current && poseData.needsCorrection !== false) {
        scheduleCorrection(poseData);
      }
    });
  }, [isSpeechEnabled, speak, scheduleCorrection]);

  // Function to request the current pose from the backend
  const requestPoseUpdate = useCallback(() => {
    return sendWebSocketMessage({ type: 'get_current_pose' });
  }, [sendWebSocketMessage]);

  // Pose detection loop - ensure it captures fresh pose data on each frame
  const detectPose = useCallback(async (detector: poseDetection.PoseDetector) => {
    // Exit immediately if not detecting - this prevents the infinite loop
    if (!isDetectingRef.current) {
      console.log('Detection loop stopped: isDetecting is false');
      return;
    }
    
    if (!videoRef.current || !canvasRef.current) {
      console.log('Detection loop aborted: missing video or canvas refs');
      return;
    }
    
    try {
      // Double-check detection state before continuing
      if (!isDetectingRef.current) {
        console.log('Detection loop interrupted: detection state changed');
        return;
      }
      
      // Ensure video is playing and has dimensions
      if (videoRef.current.readyState < 2 || videoRef.current.videoWidth === 0) {
        // Video not ready yet, continue the loop but log the issue
        console.log('Video not ready in detection loop, readyState:', videoRef.current.readyState);
        
        // Only request the next frame if we're still detecting
        if (isDetectingRef.current) {
          animationRef.current = requestAnimationFrame(() => detectPose(detector));
        }
        return;
      }
      
      // Force canvas dimensions to exactly match video
      if (canvasRef.current && videoRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          // Always update canvas dimensions to exactly match video dimensions
          if (videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
            canvasRef.current.width = videoRef.current.videoWidth;
            canvasRef.current.height = videoRef.current.videoHeight;
          }
          
          // Clear canvas before detection to avoid ghosting
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
      
      // Detect poses in the video frame
      const poses = await detector.estimatePoses(videoRef.current);
      
      // Update state with the first detected pose
      if (poses.length > 0) {
        // Save the latest detected pose - this is what will be used for corrections
        setPose(poses[0]);
        setPoseKeypoints(poses[0].keypoints);
        
        // CRITICAL FIX: Store the current pose in a ref for immediate access
        // This ensures we always have the latest pose data available for corrections
        currentPoseRef.current = poses[0];
        
        // Draw pose on canvas immediately after detection
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) {
            // Get canvas dimensions for calculating effects
            const canvasWidth = canvasRef.current.width;
            const canvasHeight = canvasRef.current.height;
            
            // Create a map of keypoints by name for easier lookup
            const keypointMap = new Map();
            poses[0].keypoints.forEach((keypoint: any) => {
              if (keypoint.name) {
                keypointMap.set(keypoint.name, keypoint);
              }
            });
            
            // Create gradient for artistic effect
            const gradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
            GRADIENT_COLORS.forEach((color, index) => {
              gradient.addColorStop(index / (GRADIENT_COLORS.length - 1), color);
            });
            
            // Set up canvas for artistic rendering
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            // Draw connections with beautiful gradient and glow
            ctx.shadowBlur = 10;
            ctx.shadowColor = 'rgba(255, 255, 255, 0.7)';
            ctx.strokeStyle = gradient;
            
            // Draw connections first (so they appear behind the keypoints)
            POSE_CONNECTIONS.forEach(([start, end]) => {
              const startPoint = keypointMap.get(start);
              const endPoint = keypointMap.get(end);
              
              if (startPoint && endPoint && startPoint.score && endPoint.score &&
                  startPoint.score > 0.3 && endPoint.score > 0.3) {
                ctx.beginPath();
                ctx.moveTo(startPoint.x, startPoint.y);
                
                // Draw flowing curved lines instead of straight lines
                // Calculate control point for quadratic curve
                const cpX = (startPoint.x + endPoint.x) / 2;
                const cpY = (startPoint.y + endPoint.y) / 2;
                const offset = 10 * Math.sin(Date.now() / 1000); // Gentle animation
                
                ctx.quadraticCurveTo(
                  cpX + offset, 
                  cpY + offset, 
                  endPoint.x, 
                  endPoint.y
                );
                
                ctx.stroke();
              }
            });
            
            // Draw keypoints on top with minimalistic style
            ctx.shadowBlur = 15;
            ctx.fillStyle = '#ffffff';
            
            poses[0].keypoints.forEach((keypoint: any) => {
              if (keypoint.score && keypoint.score > 0.3) {
                // Animate point size slightly based on time
                const pointSize = 4 + 2 * Math.sin(Date.now() / 500 + keypoint.x / 100);
                
                // Draw minimalistic dot
                ctx.beginPath();
                ctx.arc(keypoint.x, keypoint.y, pointSize, 0, 2 * Math.PI);
                ctx.fill();
              }
            });
            
            // Reset shadow for better performance
            ctx.shadowBlur = 0;
          }
        }
      }
      
      // Schedule next frame only if we're still detecting
      if (isDetectingRef.current) {
        // Use immediate callback for smoother animation
        animationRef.current = requestAnimationFrame(() => detectPose(detector));
      }
    } catch (error) {
      // On error, only continue if we're still detecting
      if (isDetectingRef.current) {
        animationRef.current = requestAnimationFrame(() => detectPose(detector));
      }
    }
  }, []);
  
  // Function to stop pose detection
  const stopDetection = useCallback(() => {
    // Cancel any ongoing speech
    cancelSpeech();
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // Update state
    setIsDetecting(false);
    isDetectingRef.current = false;
    
    // Do NOT close the WebSocket connection here - keep it open for transitions
    // Also don't stop the camera stream here to avoid reinitialization issues
  }, [cancelSpeech]);

  // Function to move to the next pose in the sequence
  const nextPose = useCallback(() => {
    // Don't allow rapid transitions - debounce this function
    if (isTransitioning) return;
    
    // Set transitioning state to prevent multiple clicks
    setIsTransitioning(true);
    
    // Invalidate the current pose ID immediately to abort any pending processes
    currentPoseIdRef.current = "";
    console.log("Invalidated current pose ID to prevent stale corrections");
    
    // Reset the correction generation flag to ensure each pose gets a correction
    hasGeneratedCorrectionRef.current = false;
    console.log("Reset correction generation flag for next pose");
    
    // Cancel any scheduled correction generation for the previous pose
    if (correctionTimeoutRef.current) {
      console.log("Cancelling pending correction timeout when moving to next pose");
      clearTimeout(correctionTimeoutRef.current);
      correctionTimeoutRef.current = null;
    }
    
    // Cancel any speech first
    cancelSpeech();
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    
    // Clear timer for current pose
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Make sure we have current sequence and pose data
    if (!allCourseData || !allCourseData.sequences) {
      setIsTransitioning(false);
      return;
    }
    
    // Get current sequence
    const currentSequence = allCourseData.sequences[currentSequenceIndex];
    if (!currentSequence || !currentSequence.poses) {
      setIsTransitioning(false);
      return;
    }
    
    // Calculate next pose index
    const nextPoseIndex = currentPoseIndex + 1;
    
    // Check if we're at the end of this sequence
    if (nextPoseIndex >= currentSequence.poses.length) {
      // Cancel any ongoing speech before completing sequence
      cancelSpeech();
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      
      // Set sequence as complete
      setSequenceComplete();
      setIsTransitioning(false);
      return;
    }
    
    // Update current pose index
    setCurrentPoseIndex(nextPoseIndex);
    
    // Get the next pose data
    const nextPoseData = currentSequence.poses[nextPoseIndex];
    
    // Clear the played instructions tracker for the new pose
    playedInstructionsForPoseNames.current.clear();
    
    // Update the current pose data
    setCurrentPoseData(nextPoseData);
    setCurrentSequencePose(nextPoseData.name);
    currentSequencePoseRef.current = nextPoseData.name;
    
    console.log(`Transitioned to new pose: ${nextPoseData.name}`);
    
    // Allow the next transition after a cooldown period
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [allCourseData, currentSequenceIndex, currentPoseIndex, cancelSpeech, isTransitioning]);

  // Function to set sequence as complete
  const setSequenceComplete = () => {
    console.log("Setting sequence complete, keeping detection active");
    // Cancel speech and clear timers
    cancelSpeech();
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Mark this sequence as complete but DO NOT stop detection
    setIsSequenceComplete(true);
    
    // Log camera state for debugging
    if (videoRef.current?.srcObject) {
      console.log("Camera status at sequence complete: Stream Active");
    } else {
      console.log("Camera status at sequence complete: No Stream");
    }
  };

  // Fix the start detection function by using non-null assertion for the detector
  const startDetection = useCallback(async () => {
    if (!isCourseLoaded) {
      setError('Please load a course first');
      return;
    }
    
    if (isReady && !isDetecting) {
      try {
        setStatusMessage('Starting yoga session...');
        setError('');
        
        // Reset pose index but keep sequence index to start the selected sequence
        setCurrentPoseIndex(0);
        
        // Clear any previous state data
        setCurrentSequencePose('');
        setPose(null);
        setPoseKeypoints([]);
        setCurrentPoseData(null);
        
        // Clear the played instructions tracker when starting detection
        playedInstructionsForPoseNames.current.clear();
        
        // Clear the correction tracking for the new session
        console.log('Clearing correction history for new detection session');
        hasGeneratedCorrectionRef.current = false;
        
        // Set detecting state
        setIsDetecting(true);
        isDetectingRef.current = true;
        
        // Check if we have course data loaded
        if (!allCourseData || !allCourseData.sequences || allCourseData.sequences.length === 0) {
          throw new Error('Course data not loaded properly. Please refresh the page.');
        }
        
        // Get the current sequence based on the current index
        const currentSequence = allCourseData.sequences[currentSequenceIndex];
        
        if (!currentSequence || !currentSequence.poses || currentSequence.poses.length === 0) {
          throw new Error('Sequence data not loaded properly. Please refresh the page.');
        }
        
        const firstPose = currentSequence.poses[0];
        
        // Check if camera stream is already active
        const cameraAlreadyActive = videoRef.current && 
                                   videoRef.current.srcObject && 
                                   videoRef.current.readyState >= 2;
        
        // Set up camera stream using pre-initialized resources
        if (!cameraAlreadyActive) {
          console.log('Starting new camera stream');
          try {
            // Use the same camera constraints that we tested during initialization
            const constraints = {
              video: {
                width: { ideal: 640 },
                height: { ideal: 480 },
                facingMode: 'user'
              },
              audio: false
            };
            
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            
            if (videoRef.current) {
              // Stop any existing stream
              if (videoRef.current.srcObject) {
                const oldStream = videoRef.current.srcObject as MediaStream;
                oldStream.getTracks().forEach(track => track.stop());
              }
              
              // Set the new stream
              videoRef.current.srcObject = stream;
              videoRef.current.setAttribute('playsinline', 'true');
              
              // Start playing the video immediately
              try {
                await videoRef.current.play();
              } catch (e) {
                throw new Error('Failed to start video playback. Please check browser permissions.');
              }
            }
          } catch (error) {
            throw new Error('Failed to access camera. Please check camera permissions.');
          }
        } else {
          console.log('Reusing existing camera stream - no need to reinitialize');
        }

        // Ensure canvas is properly initialized
        if (canvasRef.current && videoRef.current) {
          // Set initial canvas dimensions to match video
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) {
            canvasRef.current.width = videoRef.current.videoWidth || 640;
            canvasRef.current.height = videoRef.current.videoHeight || 480;
          }
        }

        // Cancel any existing speech before starting new detection
        cancelSpeech();

        // Start detection loop using the pre-initialized detector
        if (detectorRef.current) {
          const detector = detectorRef.current;
          // Use non-null assertion because we've already checked detector exists
          animationRef.current = requestAnimationFrame(() => detectPose(detector!));
        } else {
          // If detector isn't available, create it now (fallback)
          // Use MoveNet for reliable and lighter yoga pose detection
          const model = poseDetection.SupportedModels.MoveNet;
          console.log('Initializing MoveNet model...');
          
          const detector = await poseDetection.createDetector(model, {
            modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
            enableSmoothing: true,
            minPoseScore: 0.2
          });
          
          console.log('MoveNet model initialized successfully');
          detectorRef.current = detector;
          setDetector(detector);
          animationRef.current = requestAnimationFrame(() => detectPose(detector));
        }
        
        // Set the first pose data to start the session
        setCurrentPoseData(firstPose);
        setCurrentSequencePose(firstPose.name);
        currentSequencePoseRef.current = firstPose.name;
        
        // Instructions will be spoken via the useEffect that watches currentPoseData
        
        setStatusMessage('');
      } catch (error) {
        setError(`Failed to start the yoga session: ${error instanceof Error ? error.message : 'Unknown error'}`);
        
        // Reset detection state
        setIsDetecting(false);
        isDetectingRef.current = false;
        setStatusMessage('');
      }
    }
  }, [isReady, isDetecting, detectPose, allCourseData, currentSequenceIndex, cancelSpeech, isCourseLoaded]);

  // Function to handle continuing to the next sequence - fix the detector reference
  const handleContinueToNextSequence = useCallback((forcedSequenceIndex?: number) => {
    // Hide the sequence complete screen
    setIsSequenceComplete(false);
    setIsSequenceTransitioning(false);
    
    // Check if we have course data loaded
    if (!allCourseData || !allCourseData.sequences || allCourseData.sequences.length === 0) {
      setError('Sequence data not available. Please refresh the page.');
      return;
    }
    
    // Use the forced sequence index if provided, otherwise use the current one
    const sequenceIndexToUse = forcedSequenceIndex !== undefined ? forcedSequenceIndex : currentSequenceIndex;
    
    // Get the sequence data based on the index to use
    const sequence = allCourseData.sequences[sequenceIndexToUse];
    
    if (!sequence || !sequence.poses || sequence.poses.length === 0) {
      setError('No poses found in this sequence. Please try another sequence.');
      return;
    }
    
    // Set the first pose of the current sequence
    const firstPose = sequence.poses[0];
    
    // Make sure detection is active
    setIsDetecting(true);
    isDetectingRef.current = true;
    
    // Clear everything before setting new data
    setCurrentPoseIndex(0);
    
    // Clear the instructions tracker when starting a new sequence
    playedInstructionsForPoseNames.current.clear();
    
    // Clear the correction tracking for the new sequence
    hasGeneratedCorrectionRef.current = false; // Reset correction flag for the new sequence
    
    // First clear all existing data
    setCurrentSequencePose('');
    setCurrentPoseData(null);
    setPose(null);
    setPoseKeypoints([]);
    
    // Ensure the animation frame is active
    if (!animationRef.current && detectorRef.current && videoRef.current) {
      animationRef.current = requestAnimationFrame(() => detectPose(detectorRef.current!));
    }
    
    // Start the detection loop if detector exists
    if (detectorRef.current && videoRef.current) {
      const detector = detectorRef.current;
      
      // Check if video is still active, if not restart the camera
      if (!videoRef.current.srcObject || videoRef.current.readyState < 2) {
        // Start camera with a slight delay to ensure UI is updated
        setTimeout(async () => {
          try {
            // IMPORTANT: First check if the video stream is actually gone
            // This helps prevent unnecessary camera restarts
            if (videoRef.current && (!videoRef.current.srcObject || videoRef.current.readyState < 2)) {
              const constraints = {
                video: {
                  width: { ideal: 640 },
                  height: { ideal: 480 },
                  facingMode: 'user'
                },
                audio: false
              };
              
              const stream = await navigator.mediaDevices.getUserMedia(constraints);
              
              if (videoRef.current) {
                // CRITICAL: Cancel any existing animation frame to prevent duplicates
                if (animationRef.current) {
                  cancelAnimationFrame(animationRef.current);
                  animationRef.current = null;
                }
                
                videoRef.current.srcObject = stream;
                
                // Use a promise to ensure we wait for the video to play
                await videoRef.current.play().catch(e => {
                  console.error('Failed to play video:', e);
                });
                
                // Wait for video to be ready before starting detection
                videoRef.current.onloadeddata = () => {
                  // ALWAYS restart the animation frame after camera restarts
                  // don't check if it's null since we want to ensure a fresh start
                  if (detectorRef.current) {
                    animationRef.current = requestAnimationFrame(() => detectPose(detectorRef.current!));
                  }
                };
              }
            }
          } catch (error) {
            console.error('Failed to restart camera:', error);
            setError('Camera access failed. Please refresh the page.');
          }
        }, 200);
      } else {
        // Make sure animation is running
        if (!animationRef.current) {
          animationRef.current = requestAnimationFrame(() => detectPose(detector));
        }
      }
      
      // Give state updates time to clear previous data before setting new data
      setTimeout(() => {
        // Set the pose data for the first pose in the sequence
        setCurrentPoseData(firstPose);
        setCurrentSequencePose(firstPose.name);
        currentSequencePoseRef.current = firstPose.name;
      }, 50);
    } else {
      // If detector or video is missing, restart detection
      startDetection();
    }
  }, [allCourseData, currentSequenceIndex, detectPose, startDetection]);

  // Move to the next sequence
  const moveToNextSequence = () => {
    // Cancel any ongoing speech before transition
    cancelSpeech();
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    
    // If the course is already marked complete, redirect to course selection
    if (isCourseComplete) {
      // Reset everything and show course selection
      handleNewCourse();
      return;
    }
    
    // Check if this was the last sequence
    if (currentSequenceIndex >= courseData.sequences.length - 1) {
      // This was the last sequence, mark the course as complete
      setIsCourseComplete(true);
      
      // IMPORTANT: Properly clean up all detection resources
      // Stop detection when course is complete
      setIsDetecting(false);
      isDetectingRef.current = false;
      
      // Explicitly cancel animation frame to stop the detection loop
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Cancel any ongoing speech
      cancelSpeech();
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      return;
    }

    // Move to the next sequence
    const nextIndex = currentSequenceIndex + 1;
    
    setCurrentSequenceIndex(nextIndex);
    // Reset pose index to start from first pose
    setCurrentPoseIndex(0);
    
    // Ensure we maintain detection throughout the transition
    if (!isDetecting) {
      setIsDetecting(true);
    }
    
    // Check if camera needs to be restarted
    if (!videoRef.current?.srcObject) {
      // Restart camera if needed
      if (videoRef.current) {
        // Initialize camera directly with code similar to startDetection function
        setTimeout(async () => {
          try {
            const constraints = {
              video: {
                width: { ideal: 640 },
                height: { ideal: 480 },
                facingMode: 'user'
              },
              audio: false
            };
            
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              
              // Use a promise to ensure we wait for the video to play
              await videoRef.current.play().catch(e => {
                console.error('Failed to play video:', e);
              });
              
              // Wait for video to be ready before continuing
              videoRef.current.onloadeddata = () => {
                // Restart animation frame if detector is ready
                if (detectorRef.current && !animationRef.current) {
                  animationRef.current = requestAnimationFrame(() => detectPose(detectorRef.current!));
                }
              };
            }
          } catch (error) {
            console.error('Failed to restart camera during transition:', error);
          }
        }, 100);
      }
    } else {
      // Make sure animation frame is still running
      if (!animationRef.current) {
        startDetection();
      }
    }
    
    // Wait a moment for everything to update before going to the next pose
    setTimeout(() => {
      // Start with the first pose in the new sequence
      const newSequence = courseData.sequences[nextIndex];
      if (newSequence && newSequence.poses && newSequence.poses.length > 0) {
        // Load the first pose data
        setCurrentPoseData(newSequence.poses[0]);
        setCurrentSequencePose(newSequence.poses[0].name);
      }
    }, 250);
  };

  // Handle restarting the course
  const handleRestartCourse = useCallback(() => {
    // Hide the course complete screen
    setIsCourseComplete(false);
    
    // Start detection for the restarted course
    startDetection();
  }, [startDetection]);
  
  // Handle choosing a new course
  const handleNewCourse = useCallback(() => {
    // For now, just restart the same course
    handleRestartCourse();
    
    // In the future, this could show a course selection UI
  }, [handleRestartCourse]);

  // Add useEffect to initialize camera and TensorFlow.js on component mount
  useEffect(() => {
    // Set initial state
    setIsReady(false);
    setStatusMessage('Initializing...');
    setIsInitializing(true);
    
    // Initialize speech synthesis
    initSpeechSynthesis();
    
    // Initialize camera and TensorFlow model before enabling Start button
    const initializeComponents = async () => {
      try {
        // Check if navigator.mediaDevices is available
        if (!navigator.mediaDevices) {
          throw new Error('Browser API navigator.mediaDevices not available');
        }
        
        setStatusMessage('Setting up camera...');
        
        // Request camera access but don't start the stream yet
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          },
          audio: false,
        });
        
        // Store the stream temporarily to verify camera is working
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          // Wait for video to be ready
          await new Promise((resolve) => {
            if (videoRef.current) {
              videoRef.current.onloadedmetadata = () => {
                resolve(true);
              };
            }
          });

          // Try to play the video briefly to test autoplay
          try {
            await videoRef.current.play();
            // Pause it immediately - we'll resume when user clicks Start
            videoRef.current.pause();
          } catch (e) {
          }
          
          // Stop the stream - we'll restart it when the user clicks Start
          stream.getTracks().forEach(track => track.stop());
          videoRef.current.srcObject = null;
        }
        
        setStatusMessage('Loading TensorFlow.js models...');
        
        // Pre-load TensorFlow.js
        const tf = await import('@tensorflow/tfjs');
        
        // Import and register WebGL backend explicitly
        try {
          const webgl = await import('@tensorflow/tfjs-backend-webgl');
        } catch (e) {
        }
        
        // Try to set WebGL backend
        try {
          await tf.setBackend('webgl');
        } catch (e) {
        }
        
        await tf.ready();
        
        // Pre-initialize the detector model
        setStatusMessage('Loading pose detection model...');
        
        try {
          // Load MoveNet for better compatibility and performance
          const model = poseDetection.SupportedModels.MoveNet;
          const detector = await poseDetection.createDetector(model, {
            modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
            enableSmoothing: true,
            minPoseScore: 0.2
          });
          
          detectorRef.current = detector;
          setDetector(detector);
        } catch (e) {
          console.error('Model initialization error:', e);
          // Try with a simpler configuration as fallback
          try {
            console.log('Trying fallback initialization with minimal config...');
            const model = poseDetection.SupportedModels.MoveNet;
            const detector = await poseDetection.createDetector(model);
            console.log('Fallback model initialized successfully');
            detectorRef.current = detector;
            setDetector(detector);
          } catch (fallbackError) {
            console.error('Fallback initialization failed:', fallbackError);
            throw new Error('Failed to pre-initialize pose detection model');
          }
        }
        
        // Now we're ready to start when the user clicks the button
        setIsReady(true);
        setStatusMessage('Ready to start');
        setIsInitializing(false);
        
      } catch (error) {
        setError(`Setup failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setIsInitializing(false);
        setStatusMessage('Failed to initialize');
      }
    };
    
    initializeComponents();
    
    return () => {
      // Cleanup if needed
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  // Initialize camera and TensorFlow.js when isDetecting becomes true
  useEffect(() => {
    if (!isDetecting) {
      return; // Don't initialize camera until user starts detection
    }

    // WebSocket connection will be handled by the dedicated WebSocket effect

    // Only stop the camera on component unmount, not when isDetecting changes
    // This ensures continuity between sequences
    return () => {
      // This cleanup only runs when component is unmounted, not between sequences
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
        });
        videoRef.current.srcObject = null;
        videoRef.current.onloadeddata = null;
      }
      
      // Cancel animation frame
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isDetecting]);

  // Load the course data
  const loadCourseData = useCallback(async (): Promise<boolean> => {
    if (initialCourseData) {
      // Course data is already loaded from the prop
      return true;
    }
    
    // If no initialCourseData is provided, show an error
    setError('No course data provided. Please navigate back to the main application.');
    return false;
  }, [initialCourseData]);

  // Add useEffect to update timer and progress bar at regular intervals
  useEffect(() => {
    // Only set up timer if we're detecting and have current pose data with duration
    if (!isDetecting || !currentPoseData || currentPoseData.duration === undefined) return;

    // Clear any existing timer interval first
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Get the initial time values - always start fresh with the full duration
    // This fixes the issue where time was carrying over between poses
    let initialTimeRemaining = currentPoseData.duration;
    const initialDuration = currentPoseData.duration || 30; // Default to 30s if not specified
    const startTime = Date.now();
    
    // Update timer and progress every 100ms for smoother display
    const timerInterval = setInterval(() => {
      // Calculate elapsed time since start of this timer
      const elapsedSeconds = (Date.now() - startTime) / 1000;
      
      // Calculate new remaining time based on elapsed time
      const newTimeRemaining = Math.max(0, initialTimeRemaining - elapsedSeconds);
      
      // Calculate new progress percentage
      const newProgressPercent = Math.min(100, Math.max(0, 
        ((initialDuration - newTimeRemaining) / initialDuration) * 100
      ));
      
      // Update the state with new timer values
      setCurrentPoseData((prevData: any) => {
        if (!prevData) return null;
        
        // Keep the same object reference for all properties except time-related ones
        return {
          ...prevData,
          time_remaining: newTimeRemaining,
          progress_percent: newProgressPercent
        };
      });
      
      // Also update the separate remainingTime state for components that might use it
      setRemainingTime(newTimeRemaining);
      
      // Auto-transition to next pose if timer reaches zero
      if (newTimeRemaining <= 0 && !isTransitioning) {
        nextPose();
        // Clear this interval since we're transitioning
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }
    }, 100);
    
    // Store interval reference so we can clear it
    timerIntervalRef.current = timerInterval;
    
    // Clean up on unmount or when dependencies change
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isDetecting, currentPoseData?.name, isTransitioning, nextPose]);

  // Update to speak instructions when currentPoseData changes
  useEffect(() => {
    // Skip if missing pose data or during transitions or not detecting
    if (!currentPoseData || isTransitioning || !isDetecting) return;
    
    const poseName = currentPoseData.name;
    
    // Extra safety check for pose name
    if (!poseName) return;
    
    // If this is a new pose, speak the instructions
    if (!playedInstructionsForPoseNames.current.has(poseName)) {
      // Add to the set of spoken pose names
      playedInstructionsForPoseNames.current.add(poseName);
      
      // Small delay to ensure we have the correct pose data
      setTimeout(() => {
        // Verify that currentPoseData is still the same before speaking
        if (currentPoseData?.name === poseName) {
          speakPoseInstructions(currentPoseData);
        }
      }, 100);
    }
  }, [currentPoseData?.id, isTransitioning, isDetecting, speakPoseInstructions]);

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      console.log("Component unmounting: full cleanup");
      
      // Update detection state first to stop any loops
      isDetectingRef.current = false;
      
      // Cancel any ongoing animation frames
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Cancel any ongoing speech
      cancelSpeech();
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      
      // Stop camera stream if active
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
        });
        videoRef.current.srcObject = null;
        videoRef.current.onloadeddata = null;
      }
      
      // Clean up WebSocket connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Clear any timer intervals
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      // Clear any detection related data
      detectorRef.current = null;
    };
  }, [cancelSpeech]);

  // Effect to ensure video element maintains its loaded data event handler
  useEffect(() => {
    // Set up the onloadeddata event handler for video
    if (videoRef.current) {
      videoRef.current.onloadeddata = () => {
        // Ensure detection animation frame is active after video loads
        if (isDetecting && detectorRef.current && !animationRef.current) {
          animationRef.current = requestAnimationFrame(() => detectPose(detectorRef.current!));
        }
      };
    }

    return () => {
      // Clean up the event handler when component unmounts
      if (videoRef.current) {
        videoRef.current.onloadeddata = null;
      }
    };
  }, [isDetecting, detectPose]);

  // Effect to handle course completion cleanup
  useEffect(() => {
    if (isCourseComplete) {
      // Make sure we clean up all detection resources
      console.log("Course complete effect: ensuring all resources are cleaned up");
      
      // Stop detection state
      isDetectingRef.current = false;
      
      // Cancel animation frame
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Cancel any ongoing speech
      cancelSpeech();
      
      // Clear any timers
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [isCourseComplete, cancelSpeech]);

  // Update effect to handle pose initialization when using speech
  useEffect(() => {
    // Skip if not speaking or missing pose data
    if (!isSpeaking || !currentPoseData) return;
    
    // Track when speech ends - for debugging purposes only
    const checkPoseTimeout = setTimeout(() => {
      if (!isSpeaking && currentPoseData && isDetectingRef.current) {
        console.log('SPEECH COMPLETED: Ready for correction if needed');
      }
    }, 500);
    
    return () => clearTimeout(checkPoseTimeout);
  }, [isSpeaking, currentPoseData, isDetectingRef]);

  // Add useEffect to handle initialCourseData
  useEffect(() => {
    if (initialCourseData && !isCourseLoaded) {
      console.log('Loading course from API data:', initialCourseData);
      
      // Process the course data
      setCourseData({
        courseName: initialCourseData.name,
        description: initialCourseData.description,
        totalSequences: initialCourseData.sequences?.length || 0,
        completedSequences: 0,
        sequences: initialCourseData.sequences || []
      });
      
      // Set the full course data
      setAllCourseData(initialCourseData);
      
      // Mark course as loaded
      setIsCourseLoaded(true);
      
      // Show success message
      setSuccessMessage('Course loaded successfully! Click "Start Course" to begin.');
    }
  }, [initialCourseData, isCourseLoaded]);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100vh',
      backgroundColor: '#000',
      position: 'relative'
    }}>
      {/* Remove the CourseImporter component 
      {showCourseImporter && (
        <CourseImporter
          onImportSuccess={handleCourseImportSuccess}
          onCancel={() => setShowCourseImporter(false)}
        />
      )}
      */}
      
      {/* Always render video and canvas but conditionally show UI screens on top */}
      <div style={{ 
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: '800px',
        maxHeight: '600px',
        margin: 'auto'
      }}>
        <video 
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            transform: 'scaleX(-1)',
            backgroundColor: '#111',
            // Only hide video visually when not detecting or on course complete
            visibility: (!isDetecting && !isSequenceComplete) || isCourseComplete ? 'hidden' : 'visible',
            // Also completely disable video processing when course is complete
            display: isCourseComplete ? 'none' : 'block'
          }}
        />
        <canvas 
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            transform: 'scaleX(-1)',
            pointerEvents: 'none',
            // Only hide canvas visually when not detecting or on course complete
            visibility: (!isDetecting && !isSequenceComplete) || isCourseComplete ? 'hidden' : 'visible',
            // Also completely disable canvas when course is complete
            display: isCourseComplete ? 'none' : 'block'
          }}
        />
        
        {/* Overlay UI screens based on current state */}
        {isCourseComplete && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 10
          }}>
            <CourseComplete 
              courseName={courseData.courseName}
              description={courseData.description || ""}
              totalSequences={courseData.totalSequences || 0}
              completedSequences={courseData.completedSequences || 0}
              onRestartCourse={handleRestartCourse}
              onNewCourse={handleNewCourse}
            />
          </div>
        )}
        
        {isSequenceComplete && !isCourseComplete && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 10
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.85)',
              color: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              textAlign: 'center',
              boxShadow: '0 0 20px rgba(0,255,0,0.3)'
            }}>
              <h2 style={{ 
                fontSize: '28px',
                marginBottom: '20px',
                color: '#4caf50'
              }}>
                Sequence Complete! 🎉
              </h2>
              <p style={{ fontSize: '16px', marginBottom: '25px' }}>
                Congratulations! You've successfully completed this yoga sequence.
              </p>
              
              {/* Single, clear call-to-action button */}
                <button
                onClick={moveToNextSequence}
                  style={{
                  padding: '15px 30px',
                  fontSize: '18px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  width: '100%',
                  maxWidth: '300px'
                  }}
                >
                Continue to Next Sequence
                </button>
            </div>
          </div>
        )}
        
        {!isDetecting && !isSequenceComplete && !isCourseComplete && (
          // Course Information and Start Button (initial screen)
          <div style={{ 
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
            zIndex: 2,
            width: '100%'
          }}>
            {/* Start Course Button - Moved to top */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '10px', 
              width: '100%', 
              maxWidth: '300px',
              marginBottom: '20px'
            }}>
              <button 
                onClick={startDetection}
                disabled={!isReady || !isCourseLoaded}
                style={{
                  padding: '15px 30px',
                  fontSize: '20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isReady && isCourseLoaded) ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  opacity: (isReady && isCourseLoaded) ? 1 : 0.7,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  width: '100%',
                  fontWeight: '500'
                }}
              >
                Start Course
              </button>
              
              {/* Course loading buttons - Now only shows messages since we use API loading */}
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                width: '100%',
                justifyContent: 'center',
                marginTop: '10px',
                marginBottom: '10px'
              }}>
                {!isCourseLoaded && <p style={{fontSize: '14px', color: '#aaa'}}>Loading course from API...</p>}
                
                {successMessage && (
                  <div style={{ 
                    backgroundColor: 'rgba(40,167,69,0.1)', 
                    color: '#28a745',
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    marginBottom: '10px',
                    textAlign: 'center',
                    width: '100%'
                  }}>
                    {successMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Course Information Panel */}
            <div style={{
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '25px',
              borderRadius: '12px',
              textAlign: 'center',
              maxWidth: '450px',
              boxShadow: '0 6px 12px rgba(0,0,0,0.3)'
            }}>
              {!isCourseLoaded ? (
                // Show this when no course is loaded
                <div>
                  <h2 style={{ color: '#4caf50', marginBottom: '15px', fontSize: '28px', fontWeight: '500' }}>
                    Welcome to YogAI
                  </h2>
                  
                  <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5', fontWeight: '300' }}>
                    Loading your course data...
                  </p>
                  
                  <div style={{ 
                    backgroundColor: 'rgba(255,193,7,0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    marginBottom: '10px' 
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>
                      🧘
                    </div>
                    <h3 style={{ fontSize: '18px', color: '#ffc107', marginBottom: '10px', fontWeight: '500' }}>
                      Preparing Your Course
                    </h3>
                    <p style={{ fontSize: '14px', color: '#ddd' }}>
                      Please wait while we prepare your yoga course from the API.
                    </p>
                  </div>
                </div>
              ) : (
                // Show this when a course is loaded (existing code)
                <>
                  <h2 style={{ color: '#4caf50', marginBottom: '15px', fontSize: '28px', fontWeight: '500' }}>
                    {courseData.courseName || "Loading course..."}
                  </h2>
                  
                  {courseData.description && (
                    <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5', fontWeight: '300' }}>
                      {courseData.description}
                    </p>
                  )}
                  
                  {/* MoveNet model notice */}
                  <div style={{
                    backgroundColor: 'rgba(3, 169, 244, 0.1)',
                    padding: '10px',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <p style={{ fontSize: '14px', color: '#03a9f4', margin: 0, fontWeight: '500' }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }}>🌟</span>
                      Using MoveNet Lightning for fast pose detection
                    </p>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: 'rgba(76, 175, 80, 0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    marginBottom: '20px' 
                  }}>
                    <h3 style={{ fontSize: '18px', color: '#4caf50', marginBottom: '10px', fontWeight: '500' }}>
                      Course Contents
                    </h3>
                    
                    {courseData.sequences && Array.isArray(courseData.sequences) && courseData.sequences.length > 0 ? (
                      <div style={{ 
                        textAlign: 'left', 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                      }}>
                        {courseData.sequences.map((sequence: any, index: number) => (
                          <div key={index} style={{ 
                            padding: '10px', 
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderRadius: '6px'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              marginBottom: '5px'
                            }}>
                              <div style={{ 
                                backgroundColor: '#4caf50',
                                color: 'white',
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: '14px',
                                fontWeight: '500'
                              }}>
                                {sequence.position || index + 1}
                              </div>
                              <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '500' }}>{sequence.name}</h4>
                            </div>
                            {sequence.description && (
                              <p style={{ margin: '0 0 0 38px', fontSize: '14px', color: '#aaa', fontWeight: '300' }}>
                                {sequence.description}
                              </p>
                            )}
                            {sequence.poses && Array.isArray(sequence.poses) && sequence.poses.length > 0 && (
                              <div style={{ margin: '5px 0 0 38px' }}>
                                <p style={{ fontSize: '14px', margin: '0 0 5px 0', color: '#03a9f4', fontWeight: '500' }}>
                                  {sequence.poses.length} pose{sequence.poses.length !== 1 ? 's' : ''}
                                </p>
                                <div style={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap',
                                  gap: '5px',
                                  margin: '5px 0 0 0'
                                }}>
                                  {sequence.poses.slice(0, 3).map((pose: any, idx: number) => (
                                    <div key={idx} style={{
                                      backgroundColor: 'rgba(3, 169, 244, 0.1)',
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: '#03a9f4',
                                      fontWeight: '300'
                                    }}>
                                      {pose.name}
                                    </div>
                                  ))}
                                  {sequence.poses.length > 3 && (
                                    <div style={{
                                      backgroundColor: 'rgba(3, 169, 244, 0.05)',
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: '#03a9f4',
                                      fontWeight: '300'
                                    }}>
                                      +{sequence.poses.length - 3} more
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        padding: '20px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '8px'
                      }}>
                        <p style={{ fontSize: '16px', color: '#aaa', marginBottom: '10px', fontWeight: '300' }}>
                          Loading course details...
                        </p>
                        <div style={{
                          width: '30px',
                          height: '30px',
                          border: '3px solid rgba(76, 175, 80, 0.3)',
                          borderTop: '3px solid #4caf50',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }}></div>
                        <style>{`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}</style>
                      </div>
                    )}
                  </div>
                  
                  <p style={{ fontSize: '16px', fontStyle: 'italic', marginBottom: '20px', color: '#aaa', fontWeight: '300' }}>
                    Get ready to improve your yoga practice with guided poses and real-time feedback!
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Status message and error display */}
        <div style={{ 
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          zIndex: 2
        }}>
          {!isReady && (
            <div style={{ 
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {statusMessage}
            </div>
          )}
        
          {error && (
            <div style={{ 
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: '#dc3545', 
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              textAlign: 'center',
              maxWidth: '400px',
              marginTop: '10px'
            }}>
              {error}
            </div>
          )}
        </div>
          
        {/* Pose information panel */}
        {isDetecting && currentPoseData && !isSequenceComplete && !isCourseComplete && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            maxWidth: '80%',
            zIndex: 5,
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#4caf50' }}>
              {currentPoseData.name}
            </h3>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
              {currentPoseData.description}
            </p>
            
            {/* Instructions section */}
            {Array.isArray(currentPoseData.instructions) && currentPoseData.instructions.length > 0 && (
              <div style={{ marginBottom: '10px', textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#03a9f4' }}>Instructions:</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px' }}>
                  {currentPoseData.instructions.map((instruction: string, index: number) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Alignment cues section */}
            {Array.isArray(currentPoseData.alignment) && currentPoseData.alignment.length > 0 && (
              <div style={{ marginBottom: '10px', textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#03a9f4' }}>Alignment:</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px' }}>
                  {currentPoseData.alignment.map((cue: string, index: number) => (
                    <li key={index}>{cue}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Pose correction info */}
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              margin: '0 0 10px 0',
              backgroundColor: currentPoseData.needsCorrection === false ? 'rgba(244, 143, 3, 0.1)' : 'rgba(3, 169, 244, 0.1)',
              borderRadius: '4px',
              padding: '4px 8px'
            }}>
              <span style={{ 
                fontSize: '14px', 
                color: currentPoseData.needsCorrection === false ? '#f48f03' : '#03a9f4' 
              }}>
                {currentPoseData.needsCorrection === false ? '✓' : '✓'}
              </span>
              <span style={{ 
                fontSize: '12px', 
                color: currentPoseData.needsCorrection === false ? '#f48f03' : '#03a9f4', 
                fontWeight: '300' 
              }}>
                {currentPoseData.needsCorrection === false 
                  ? 'Introduction pose - no corrections needed' 
                  : 'MoveNet Lightning pose tracking active'}
              </span>
            </div>
            
            {isSpeaking && (
              <div style={{ 
                fontSize: '12px', 
                color: '#ffc107', 
                marginTop: '5px', 
                fontStyle: 'italic' 
              }}>
                Speaking instructions...
              </div>
            )}
            {currentPoseData.tips && currentPoseData.tips.length > 0 && (
              <div style={{ marginTop: '10px', textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#03a9f4' }}>Tips:</h4>
                <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '12px' }}>
                  {currentPoseData.tips.map((tip: string, index: number) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
            {currentPoseData.next_pose && (
              <div style={{ 
                marginTop: '10px', 
                fontSize: '12px', 
                color: '#e91e63' 
              }}>
                Next: {currentPoseData.next_pose}
              </div>
            )}
            
            {/* Progress bar */}
            {currentPoseData.progress_percent !== undefined && (
              <div style={{ marginTop: '15px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: '12px',
                  marginBottom: '5px'
                }}>
                  <span>Time remaining: {Math.round(currentPoseData.time_remaining)}s</span>
                  <span>{Math.round(currentPoseData.progress_percent)}%</span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${currentPoseData.progress_percent}%`, 
                    height: '100%', 
                    backgroundColor: '#4caf50',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}
            
            {/* Next Pose Button */}
            <button
              onClick={nextPose}
              disabled={isTransitioning} // Disable button during transitions
              style={{
                marginTop: '15px',
                padding: '8px 16px',
                fontSize: '14px',
                backgroundColor: isTransitioning ? '#47a7f5' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isTransitioning ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s ease',
                width: '100%',
              }}
            >
              {isTransitioning ? 'Transitioning...' : 'Next Pose →'}
            </button>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(220, 53, 69, 0.9)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '5px',
          zIndex: 1100,
          textAlign: 'center',
          maxWidth: '80%'
        }}>
          <p style={{ margin: 0, fontSize: '16px' }}>{error}</p>
        </div>
      )}
    </div>
  );
};

export default PoseDetector;